import type { ChatLayoutHeaderMoreOptionsProps } from './ChatLayoutHeaderMoreOptions.types';
import { ChatLayoutContext } from 'Components/Chat/contexts/ChatLayoutContext';
import { PopOverMenuOption } from 'Components/shared/PopOverMenuOption';
import { COMMUNICATOR_MARK_AS_UNREAD } from 'Constants/featureFlags';
import useFeatureFlags from 'Hooks/featureFlags/useFeatureFlags';
import { MobXProviderContext, observer } from 'mobx-react';
import {
  useArchiveConversation,
  useMarkConversationAsRead,
  useMarkConversationAsUnread,
  useUnarchiveConversation,
} from 'Modules/conversation/conversations-list/requests/index.requests';
import { useDeleteConversationModal } from 'Modules/conversation/deleteConversation/hooks/useDeleteConversation';
import {
  canShowMarkAsReadUnread,
  canShowMuteUnmute,
} from 'Modules/conversation/guards/conversationActionChecks';
import { useIsPersonInactive } from 'Modules/conversation/hooks/useIsPersonInactive';
import { useConversationStore } from 'Modules/conversation/store/index.store';
import { useTogglePinConversation } from 'Modules/conversation/store/pinned-conversations/index.requests';
import { useUIStore } from 'Modules/ui/index.store';
import React, { useCallback } from 'react';
import type { RootStoreProps } from 'Stores/RootStore.types';
import { bugsnagClient } from 'Utils/logUtils';
import { ConfirmationModalContext } from '../contexts/ConfirmationModalContext';
import { useMuteConversation } from '../hooks/useMuteConversation';

export const ChatLayoutHeaderMoreOptions: React.FC<ChatLayoutHeaderMoreOptionsProps> =
  observer(
    ({
      conversationId,
      isGroupChat,
      isAdHocGroupChat,
      personDetails,
      setShowDropdown,
      testid,
    }) => {
      const {
        conversation: { setChannelInfoDetails, CurrentConversation },
        ui: {
          groupModalOpen,
          setGroupModal,
          setSelectedTopBarUsers,
          setOpeningGroupModalFrom,
        },
        participant: { removeParticipantsDelete },
        person: { loggedInPersonId, getUserProfileV2 },
      } = React.useContext<RootStoreProps>(MobXProviderContext);
      const { setShow: setShowConfirmationModal } = React.useContext(
        ConfirmationModalContext
      );
      const { setView } = React.useContext(ChatLayoutContext);
      const { isMuted, conversationMuteToggle } = useMuteConversation({
        conversationId,
      });

      const { mutate: togglePinConversation } = useTogglePinConversation();
      const { mutate: archiveConversation } = useArchiveConversation();
      const { mutate: unarchiveConversation } = useUnarchiveConversation();
      const { mutate: markConversationAsRead } = useMarkConversationAsRead();
      const { mutate: markConversationAsUnread } =
        useMarkConversationAsUnread();
      const { getIsConversationPinned, isPinningOrUnpinning } =
        useConversationStore();

      const { [COMMUNICATOR_MARK_AS_UNREAD]: isMarkAsUnreadEnabled } =
        useFeatureFlags([COMMUNICATOR_MARK_AS_UNREAD]);

      const isPersonInactive = useIsPersonInactive(CurrentConversation);

      const isConversationPinned = getIsConversationPinned(conversationId);

      const isConversationUnread = CurrentConversation?.unreadCount > 0;

      const showMarkAsReadUnread = canShowMarkAsReadUnread(
        CurrentConversation,
        isMarkAsUnreadEnabled
      );
      const showMuteUnmute = canShowMuteUnmute(CurrentConversation);

      const openInfoDetails = () => {
        if (personDetails) {
          useUIStore.getState().setEntitySidePanelInfo({
            entityId: personDetails.id.toString(),
            entityType: personDetails.type,
          });
        }

        if (isGroupChat) {
          useUIStore.getState().setEntitySidePanelInfo({
            groupConversationId: conversationId,
          });
        }
      };

      const showLeaveGroupModal = useCallback(() => {
        const onConfirm = async () => {
          void removeParticipantsDelete(conversationId, [
            {
              id: `${loggedInPersonId}_`,
              name: (await getUserProfileV2()).name,
            },
          ]);
        };
        setShowConfirmationModal({ type: 'leaveGroup', onConfirm });
      }, [
        conversationId,
        loggedInPersonId,
        removeParticipantsDelete,
        setShowConfirmationModal,
      ]);

      const createGroupChat = () => {
        setGroupModal(!groupModalOpen);
        setChannelInfoDetails('0', 'new', 'person');
        setSelectedTopBarUsers([`${personDetails.id}`]);
        setOpeningGroupModalFrom('top-bar');
      };

      const closeOnOptionClick = useCallback(
        async (callback: () => void | Promise<void>) => {
          await callback();
          setShowDropdown(false);
        },
        [setShowDropdown]
      );

      const LeaveGroup = useCallback(
        () =>
          !CurrentConversation?.IsSmsConversation &&
            ((isGroupChat && !isAdHocGroupChat) ||
              (isAdHocGroupChat &&
                CurrentConversation?.participants?.length > 2)) ? (
            <PopOverMenuOption
              testid={`${testid}-buttonLeaveGroup`}
              onClick={() => closeOnOptionClick(showLeaveGroupModal)}
            >
              Leave group
            </PopOverMenuOption>
          ) : null,

        [
          CurrentConversation?.participants?.length,
          CurrentConversation?.IsSmsConversation,
          closeOnOptionClick,
          isAdHocGroupChat,
          isGroupChat,
          showLeaveGroupModal,
          testid,
        ]
      );

      const handlePinConversation = useCallback(() => {
        togglePinConversation(conversationId);
        setShowDropdown(false);
      }, [conversationId, setShowDropdown, togglePinConversation]);

      const { handleDeleteConversation } = useDeleteConversationModal({
        conversation: CurrentConversation,
        onError: (error) => {
          bugsnagClient.notify(error as Error, (event) => {
            event.severity = 'error';
            event.context =
              'ChatLayoutHeaderMoreOptions.handleDeleteConversation';
            event.addMetadata('custom', {
              function: 'handleDeleteConversation',
              action: 'delete_conversation_attempt',
              conversationId: conversationId,
              code: error,
            });
            event.request = error;
          });
        },
      });
      const handleArchiveConversation = useCallback(() => {
        archiveConversation({
          conversationId,
          source: 'conversation',
        });
        setShowDropdown(false);
      }, [archiveConversation, conversationId, setShowDropdown]);

      const handleUnarchiveConversation = useCallback(() => {
        unarchiveConversation({
          conversationId,
          source: 'conversation',
        });
        setShowDropdown(false);
      }, [unarchiveConversation, conversationId, setShowDropdown]);

      const handleMarkAsRead = useCallback(() => {
        markConversationAsRead({
          conversationId,
          source: 'conversation',
        });
        setShowDropdown(false);
      }, [markConversationAsRead, conversationId, setShowDropdown]);

      const handleMarkAsUnread = useCallback(() => {
        markConversationAsUnread({
          conversationId,
          source: 'conversation',
        });
        setShowDropdown(false);
      }, [markConversationAsUnread, conversationId, setShowDropdown]);

      return (
        <>
          {!isPersonInactive && (
            <PopOverMenuOption
              testid={`${testid}-buttonViewChatInfo`}
              onClick={() => void closeOnOptionClick(openInfoDetails)}
            >
              {isGroupChat ? 'View group details' : 'View conversation details'}
            </PopOverMenuOption>
          )}
          {showMarkAsReadUnread && (
            <PopOverMenuOption
              testid={`${testid}-buttonMarkAsReadUnread`}
              onClick={() =>
                void closeOnOptionClick(
                  isConversationUnread ? handleMarkAsRead : handleMarkAsUnread
                )
              }
            >
              {isConversationUnread ? 'Mark as read' : 'Mark as unread'}
            </PopOverMenuOption>
          )}
          {showMuteUnmute && (
            <PopOverMenuOption
              testid={`${testid}-buttonMuteNotifications`}
              onClick={() => void closeOnOptionClick(conversationMuteToggle)}
            >
              {isMuted ? 'Unmute notifications' : 'Mute notifications'}
            </PopOverMenuOption>
          )}

          {!CurrentConversation?.archived && (
            <PopOverMenuOption
              testid={`${testid}-pinConversation`}
              onClick={handlePinConversation}
              disabled={isPinningOrUnpinning}
            >
              {isConversationPinned ? 'Unpin conversation' : 'Pin conversation'}
            </PopOverMenuOption>
          )}

          {!CurrentConversation?.archived ? (
            <PopOverMenuOption
              testid={`${testid}-archiveConversation`}
              onClick={() => void closeOnOptionClick(handleArchiveConversation)}
            >
              Archive conversation
            </PopOverMenuOption>
          ) : (
            <PopOverMenuOption
              testid={`${testid}-unarchiveConversation`}
              onClick={() =>
                void closeOnOptionClick(handleUnarchiveConversation)
              }
            >
              Unarchive conversation
            </PopOverMenuOption>
          )}

          <PopOverMenuOption
            testid={`${testid}-sharedFilesAndLinks`}
            onClick={() =>
              void closeOnOptionClick(() => setView('shared-files-and-links'))
            }
          >
            Shared files & links
          </PopOverMenuOption>

          {CurrentConversation?.isActiveParticipant && (
            <>
              {!isGroupChat &&
                personDetails.type === 'person' &&
                !isPersonInactive && (
                  <PopOverMenuOption
                    testid={`${testid}-buttonCreateGroupChat`}
                    onClick={() => void closeOnOptionClick(createGroupChat)}
                  >
                    Start a group chat
                  </PopOverMenuOption>
                )}

              <LeaveGroup />
            </>
          )}

          <PopOverMenuOption
            testid={`${testid}-buttonDeleteConversation`}
            onClick={() => {
              setShowDropdown(false);
              handleDeleteConversation();
            }}
          >
            Delete conversation
          </PopOverMenuOption>
        </>
      );
    }
  );
